import { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { format } from "date-fns";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Database,
  Download,
  FileSpreadsheet,
  FlaskConical,
  RefreshCw,
  Trash2,
  XCircle,
} from "lucide-react";
import prettyBytes from "pretty-bytes";
import { toast } from "sonner";

import {
  FeatureExtractionExportDownloadState,
  FeatureExtractionExportMode,
  FeatureExtractionExportStatus,
  useFeatureExtractionExportDownloadUrlMutation,
  useFeatureExtractionExportRunsQuery,
  usePesTestFeatureCacheOverviewQuery,
  usePurgePesTestFeatureCachesMutation,
  useStartFeatureExtractionExportMutation,
} from "generated/hooks";
import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { Routes } from "~/routes";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Progress } from "~/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Textarea } from "~/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import Loading from "@components/loading/Loading";
import { parseServerError } from "~/util/parseError";

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const activeStatuses = new Set<FeatureExtractionExportStatus>([
  FeatureExtractionExportStatus.Preparing,
  FeatureExtractionExportStatus.Extracting,
  FeatureExtractionExportStatus.GeneratingCsv,
]);

const splitTokens = (value: string) =>
  value
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter(Boolean);

const parseTestLimit = (value: string): number | null | undefined => {
  const normalized = value.trim();
  if (!normalized) {
    return null;
  }
  if (!/^[1-9]\d*$/.test(normalized)) {
    return undefined;
  }
  const parsed = Number(normalized);
  return Number.isSafeInteger(parsed) && parsed <= 2_147_483_647
    ? parsed
    : undefined;
};

const statusLabel = (status: FeatureExtractionExportStatus) =>
  ({
    [FeatureExtractionExportStatus.Preparing]: "Preparing",
    [FeatureExtractionExportStatus.Extracting]: "Extracting",
    [FeatureExtractionExportStatus.GeneratingCsv]: "Building CSV",
    [FeatureExtractionExportStatus.Completed]: "Complete",
    [FeatureExtractionExportStatus.CompletedWithErrors]: "Partial",
    [FeatureExtractionExportStatus.Failed]: "Failed",
  })[status];

const statusBadge = (status: FeatureExtractionExportStatus) => {
  if (status === FeatureExtractionExportStatus.Completed) {
    return { icon: CheckCircle2, className: "border-emerald-300 bg-emerald-50 text-emerald-800" };
  }
  if (status === FeatureExtractionExportStatus.CompletedWithErrors) {
    return { icon: AlertTriangle, className: "border-amber-300 bg-amber-50 text-amber-800" };
  }
  if (status === FeatureExtractionExportStatus.Failed) {
    return { icon: XCircle, className: "border-red-300 bg-red-50 text-red-800" };
  }
  return { icon: Clock3, className: "border-blue-300 bg-blue-50 text-blue-800" };
};

const modeLabel = (mode: FeatureExtractionExportMode) =>
  mode === FeatureExtractionExportMode.FromScratch
    ? "From scratch"
    : "Missing caches";

const FeatureCacheAdminPage = () => {
  const {
    data: cacheData,
    loading: cacheLoading,
    error: cacheError,
    refetch: refetchCache,
  } = usePesTestFeatureCacheOverviewQuery();
  const {
    data: runsData,
    loading: runsLoading,
    error: runsError,
    refetch: refetchRuns,
    fetchMore,
    startPolling,
    stopPolling,
  } = useFeatureExtractionExportRunsQuery({
    variables: { after: null },
    notifyOnNetworkStatusChange: true,
  });
  const [startExport, startResult] = useStartFeatureExtractionExportMutation();
  const [getDownloadUrl, downloadResult] =
    useFeatureExtractionExportDownloadUrlMutation();
  const [purgeFeatureCaches, purgeResult] =
    usePurgePesTestFeatureCachesMutation();

  const [troUuid, setTroUuid] = useState("");
  const [testLimit, setTestLimit] = useState("");
  const [mode, setMode] = useState<FeatureExtractionExportMode>(
    FeatureExtractionExportMode.MissingCurrentVersion
  );
  const [scratchConfirmOpen, setScratchConfirmOpen] = useState(false);
  const [downloadingRunId, setDownloadingRunId] = useState<string | null>(null);
  const [testIdsText, setTestIdsText] = useState("");
  const [sampleIdsText, setSampleIdsText] = useState("");
  const [featureSetVersion, setFeatureSetVersion] = useState("");
  const [confirmPurgeAllOpen, setConfirmPurgeAllOpen] = useState(false);

  const overview = cacheData?.pesTestFeatureCacheOverview;
  const runs = runsData?.featureExtractionExportRuns?.nodes ?? [];
  const pageInfo = runsData?.featureExtractionExportRuns?.pageInfo;
  const hasActiveRuns = runs.some((run) => activeStatuses.has(run.status));
  const parsedTestIds = useMemo(() => splitTokens(testIdsText), [testIdsText]);
  const parsedSampleIds = useMemo(
    () => splitTokens(sampleIdsText),
    [sampleIdsText]
  );
  const normalizedFeatureSetVersion = featureSetVersion.trim() || null;

  useEffect(() => {
    if (hasActiveRuns) {
      startPolling(5000);
      return () => stopPolling();
    }
    stopPolling();
    return undefined;
  }, [hasActiveRuns, startPolling, stopPolling]);

  const runExport = async () => {
    const normalizedTroUuid = troUuid.trim();
    if (!uuidPattern.test(normalizedTroUuid)) {
      toast.error("Enter a valid TRO UUID.");
      return;
    }
    const limit = parseTestLimit(testLimit);
    if (limit === undefined) {
      toast.error("Test limit must be a positive whole number.");
      return;
    }

    try {
      const result = await startExport({
        variables: {
          input: { troUuid: normalizedTroUuid, mode, limit },
        },
      });
      toast.success(
        `Feature extraction run ${result.data?.startFeatureExtractionExport.id ?? ""} queued.`
      );
      setScratchConfirmOpen(false);
      await refetchRuns();
    } catch (err) {
      Sentry.captureException(err);
      toast.error(`Unable to queue export: ${parseServerError(err).message}`);
    }
  };

  const requestRun = () => {
    if (!uuidPattern.test(troUuid.trim())) {
      toast.error("Enter a valid TRO UUID.");
      return;
    }
    if (parseTestLimit(testLimit) === undefined) {
      toast.error("Test limit must be a positive whole number.");
      return;
    }
    if (mode === FeatureExtractionExportMode.FromScratch) {
      setScratchConfirmOpen(true);
      return;
    }
    void runExport();
  };

  const downloadExport = async (runId: string) => {
    setDownloadingRunId(runId);
    try {
      const result = await getDownloadUrl({
        variables: { id: runId },
      });
      const download = result.data?.featureExtractionExportDownloadUrl;
      if (!download?.url) {
        toast.error("This CSV has expired from cloud storage.");
        await refetchRuns();
        return;
      }
      window.location.assign(download.url);
    } catch (err) {
      Sentry.captureException(err);
      toast.error(`Unable to download CSV: ${parseServerError(err).message}`);
    } finally {
      setDownloadingRunId(null);
    }
  };

  const loadOlderRuns = async () => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) {
      return;
    }
    await fetchMore({
      variables: { after: pageInfo.endCursor },
    });
  };

  const purgeSelected = async () => {
    const invalidTestIds = parsedTestIds.filter((id) => !uuidPattern.test(id));
    if (invalidTestIds.length > 0) {
      toast.error(`Invalid test ID: ${invalidTestIds[0]}`);
      return;
    }
    if (parsedTestIds.length === 0 && parsedSampleIds.length === 0) {
      toast.error("Enter at least one test ID or sample ID.");
      return;
    }

    try {
      const result = await purgeFeatureCaches({
        variables: {
          input: {
            testIds: parsedTestIds,
            sampleIds: parsedSampleIds,
            featureSetVersion: normalizedFeatureSetVersion,
            purgeAll: false,
            reason: "Purged from feature extraction admin page.",
          },
        },
      });
      toast.success(
        `${result.data?.purgePesTestFeatureCaches.purgedCount ?? 0} feature caches purged.`
      );
      await refetchCache();
    } catch (err) {
      toast.error(`Unable to purge caches: ${parseServerError(err).message}`);
    }
  };

  const purgeAll = async () => {
    try {
      const result = await purgeFeatureCaches({
        variables: {
          input: {
            featureSetVersion: normalizedFeatureSetVersion,
            purgeAll: true,
            reason: "Purged all feature caches from feature extraction admin page.",
          },
        },
      });
      toast.success(
        `${result.data?.purgePesTestFeatureCaches.purgedCount ?? 0} feature caches purged.`
      );
      setConfirmPurgeAllOpen(false);
      await refetchCache();
    } catch (err) {
      toast.error(`Unable to purge caches: ${parseServerError(err).message}`);
    }
  };

  return (
    <PageTemplate
      title="Feature Extraction"
      browserTitle="Feature Extraction"
      subtitle="Super Admin"
      activeItem="Super admin"
      activeSubItem="Feature Extraction"
      breadcrumbs={[
        { label: "Super admin", path: Routes.TrainedIndicators },
        { label: "Feature Extraction", path: Routes.FeatureCacheAdmin },
      ]}
    >
      <div className="space-y-6">
        <Card className="overflow-hidden border-blue-200 py-0 shadow-sm">
          <div className="grid border-b bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 px-6 py-5 text-white lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="text-xs font-semibold tracking-[0.16em] text-blue-200 uppercase">
                Dataset production
              </p>
              <h2 className="mt-1 text-xl font-semibold">Extract once. Export a compatible training table.</h2>
              <p className="mt-1 max-w-2xl text-sm text-slate-300">
                Scope by TRO, run feature extraction without prediction, then store the finished CSV in cloud storage.
              </p>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-200 lg:mt-0">
              <span className="rounded-md bg-white/10 px-2.5 py-1.5">TRO scope</span>
              <span aria-hidden="true">→</span>
              <span className="rounded-md bg-white/10 px-2.5 py-1.5">Features only</span>
              <span aria-hidden="true">→</span>
              <span className="rounded-md bg-emerald-400/20 px-2.5 py-1.5 text-emerald-100">GCS CSV</span>
            </div>
          </div>
          <CardContent className="grid gap-5 px-6 py-5 lg:grid-cols-[minmax(0,1fr)_minmax(340px,0.8fr)]">
            <div className="space-y-4">
              <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="feature-export-tro">
                TRO UUID
              </label>
              <Input
                id="feature-export-tro"
                value={troUuid}
                onChange={(event) => setTroUuid(event.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                aria-invalid={Boolean(troUuid) && !uuidPattern.test(troUuid.trim())}
              />
              <p className="text-xs text-muted-foreground">
                Failed tests and tests without a usable soil sample, lab result, or extractor input are excluded.
              </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="feature-export-limit">
                  Test limit <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <Input
                  id="feature-export-limit"
                  type="number"
                  min={1}
                  step={1}
                  value={testLimit}
                  onChange={(event) => setTestLimit(event.target.value)}
                  placeholder="e.g. 10"
                  aria-invalid={parseTestLimit(testLimit) === undefined}
                />
                <p className="text-xs text-muted-foreground">
                  Selects up to this many valid tests, ordered by test name then UUID. Leave blank for all valid tests.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <span className="text-sm font-medium">Extraction mode</span>
              <RadioGroup
                value={mode}
                onValueChange={(value) => setMode(value as FeatureExtractionExportMode)}
                className="grid gap-2 sm:grid-cols-2"
              >
                <label className="flex cursor-pointer gap-3 rounded-lg border p-3 hover:bg-muted/40">
                  <RadioGroupItem
                    value={FeatureExtractionExportMode.MissingCurrentVersion}
                    aria-label="Only missing current-version caches"
                  />
                  <span>
                    <span className="block text-sm font-medium">Only missing caches</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">Reuse valid caches for the current version.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer gap-3 rounded-lg border border-amber-200 p-3 hover:bg-amber-50/60">
                  <RadioGroupItem
                    value={FeatureExtractionExportMode.FromScratch}
                    aria-label="From scratch"
                  />
                  <span>
                    <span className="block text-sm font-medium">From scratch</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">Purge current-version caches, then extract all.</span>
                  </span>
                </label>
              </RadioGroup>
              <Button type="button" onClick={requestRun} disabled={startResult.loading} className="w-full sm:w-auto">
                <FlaskConical className="mr-2 size-4" aria-hidden="true" />
                {startResult.loading ? "Queuing run..." : "Start feature extraction"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="gap-3 py-4">
          <CardHeader className="flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="size-4" aria-hidden="true" />
                Export runs
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">Newest first. Active runs refresh every five seconds.</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => void refetchRuns()} disabled={runsLoading}>
              <RefreshCw className={`mr-2 size-3.5 ${runsLoading ? "animate-spin" : ""}`} aria-hidden="true" />
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {runsError && <p className="text-sm text-destructive">Unable to load export runs: {runsError.message}</p>}
            {runsLoading && runs.length === 0 && <Loading />}
            {!runsLoading && runs.length === 0 && !runsError && (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <FileSpreadsheet className="mx-auto size-7 text-muted-foreground" aria-hidden="true" />
                <p className="mt-2 text-sm font-medium">No feature exports yet</p>
                <p className="mt-1 text-xs text-muted-foreground">Enter a TRO UUID above to create the first run.</p>
              </div>
            )}
            {runs.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created</TableHead>
                    <TableHead>TRO</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Output</TableHead>
                    <TableHead className="text-right">Download</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((run) => {
                    const badge = statusBadge(run.status);
                    const StatusIcon = badge.icon;
                    const terminalCount = run.completedExtractionCount + run.failedExtractionCount;
                    const progress = run.totalTestCount > 0 ? Math.min(100, (terminalCount / run.totalTestCount) * 100) : 0;
                    const downloadable = run.downloadState === FeatureExtractionExportDownloadState.Available;
                    return (
                      <TableRow key={run.id} className="align-top">
                        <TableCell>
                          <div className="font-medium">{format(new Date(run.createdAt), "dd/MM/yyyy HH:mm")}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{run.requestedByEmail ?? "Superadmin"}</div>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs">{run.troUuid}</code>
                          <div className="mt-1 flex gap-1.5">
                            <Badge variant="outline">{modeLabel(run.mode)}</Badge>
                            <Badge variant="outline">{run.featureSetVersion}</Badge>
                            {run.testLimit != null && <Badge variant="outline">Limit {run.testLimit.toLocaleString()}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={badge.className}>
                            <StatusIcon className="size-3" aria-hidden="true" />
                            {statusLabel(run.status)}
                          </Badge>
                          {run.errorSummary && (
                            <details className="mt-2 max-w-72 whitespace-normal text-xs text-destructive">
                              <summary className="cursor-pointer">View errors</summary>
                              <p className="mt-1 whitespace-pre-wrap">{run.errorSummary}</p>
                            </details>
                          )}
                        </TableCell>
                        <TableCell className="min-w-52">
                          <div className="flex justify-between text-xs">
                            <span>{terminalCount.toLocaleString()} / {run.totalTestCount.toLocaleString()}</span>
                            <span>{Math.round(progress)}%</span>
                          </div>
                          <Progress value={progress} className="mt-2 h-1.5" />
                          <div className="mt-2 text-xs text-muted-foreground">
                            {run.reusedCacheCount.toLocaleString()} reused · {run.failedExtractionCount.toLocaleString()} failed · {run.excludedTestCount.toLocaleString()} excluded
                          </div>
                        </TableCell>
                        <TableCell>
                          {run.csvRowCount != null ? (
                            <div className="text-xs">
                              <div className="font-medium">{run.csvRowCount.toLocaleString()} rows × {run.csvColumnCount?.toLocaleString() ?? "—"} columns</div>
                              <div className="mt-1 text-muted-foreground">
                                {run.csvSizeBytes != null ? prettyBytes(Number(run.csvSizeBytes)) : "Size unavailable"}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">Not generated</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {run.downloadState === FeatureExtractionExportDownloadState.Expired ? (
                            <Badge variant="outline">Expired</Badge>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!downloadable || (downloadResult.loading && downloadingRunId === run.id)}
                              onClick={() => void downloadExport(run.id)}
                            >
                              <Download className="mr-2 size-3.5" aria-hidden="true" />
                              {downloadResult.loading && downloadingRunId === run.id ? "Preparing..." : "CSV"}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {pageInfo?.hasNextPage && (
              <div className="mt-4 flex justify-center">
                <Button type="button" variant="outline" onClick={() => void loadOlderRuns()} disabled={runsLoading}>
                  Load older runs
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {(cacheLoading || cacheError || overview) && (
          <section className="space-y-4" aria-labelledby="cache-inventory-title">
            <div>
              <h2 id="cache-inventory-title" className="text-lg font-semibold">Cache inventory</h2>
              <p className="text-sm text-muted-foreground">Inspect and manually remove feature caches outside a TRO export.</p>
            </div>
            {cacheLoading && <Loading />}
            {cacheError && <div>Error loading feature cache: {cacheError.message}</div>}
            {overview && (
              <>
                <div className="grid gap-4 md:grid-cols-3">
                  <Card className="gap-3 py-4">
                    <CardHeader className="pb-1"><CardTitle className="text-sm font-medium text-muted-foreground">Active caches</CardTitle></CardHeader>
                    <CardContent className="text-2xl font-semibold">{overview.activeCacheCount.toLocaleString()}</CardContent>
                  </Card>
                  <Card className="gap-3 py-4">
                    <CardHeader className="pb-1"><CardTitle className="text-sm font-medium text-muted-foreground">Tests cached</CardTitle></CardHeader>
                    <CardContent className="text-2xl font-semibold">{overview.activeTestCount.toLocaleString()}</CardContent>
                  </Card>
                  <Card className="gap-3 py-4">
                    <CardHeader className="pb-1"><CardTitle className="text-sm font-medium text-muted-foreground">Features stored</CardTitle></CardHeader>
                    <CardContent className="space-y-1">
                      <p className="text-2xl font-semibold">{overview.activeFeatureCount.toLocaleString()}</p>
                      {overview.lastExtractedAt && <p className="text-xs text-muted-foreground">Latest {format(new Date(overview.lastExtractedAt), "dd/MM/yyyy HH:mm")}</p>}
                    </CardContent>
                  </Card>
                </div>
                <Card className="gap-3 py-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2"><Database className="size-4" aria-hidden="true" />Cache controls</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="space-y-2">
                        <label className="text-sm font-medium" htmlFor="feature-cache-test-ids">Test IDs</label>
                        <Textarea id="feature-cache-test-ids" value={testIdsText} onChange={(event) => setTestIdsText(event.target.value)} placeholder="One or more PES test UUIDs" rows={5} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium" htmlFor="feature-cache-sample-ids">Sample IDs</label>
                        <Textarea id="feature-cache-sample-ids" value={sampleIdsText} onChange={(event) => setSampleIdsText(event.target.value)} placeholder="One or more extractor sample IDs" rows={5} />
                      </div>
                    </div>
                    <div className="max-w-md space-y-2">
                      <label className="text-sm font-medium" htmlFor="feature-cache-version">Feature-set version</label>
                      <Input id="feature-cache-version" value={featureSetVersion} onChange={(event) => setFeatureSetVersion(event.target.value)} placeholder="Optional" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="destructive" onClick={purgeSelected} disabled={purgeResult.loading}>
                        <Trash2 className="mr-2 size-4" aria-hidden="true" />
                        {purgeResult.loading ? "Purging..." : "Purge selected"}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setConfirmPurgeAllOpen(true)} disabled={purgeResult.loading || overview.activeCacheCount === 0}>Purge all</Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </section>
        )}
      </div>

      <AlertDialog open={scratchConfirmOpen} onOpenChange={setScratchConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-extract this TRO from scratch?</AlertDialogTitle>
            <AlertDialogDescription>
              Current-version caches for {parseTestLimit(testLimit) == null
                ? "every eligible test"
                : `up to ${parseTestLimit(testLimit)?.toLocaleString()} valid test${parseTestLimit(testLimit) === 1 ? "" : "s"}`} in TRO {troUuid.trim()} will be purged. Historic feature versions are kept. This can be an expensive run.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button type="button" variant="outline" onClick={() => setScratchConfirmOpen(false)} disabled={startResult.loading}>Cancel</Button>
            <Button type="button" variant="destructive" onClick={() => void runExport()} disabled={startResult.loading}>
              {startResult.loading ? "Queuing..." : "Purge and extract selected"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmPurgeAllOpen} onOpenChange={setConfirmPurgeAllOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Purge all feature caches</AlertDialogTitle>
            <AlertDialogDescription>
              This removes every active cached feature payload matching the current version filter. Future runs will need to extract features again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmPurgeAllOpen(false)} disabled={purgeResult.loading}>Cancel</Button>
            <Button type="button" variant="destructive" onClick={purgeAll} disabled={purgeResult.loading}>
              {purgeResult.loading ? "Purging..." : "Purge all"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageTemplate>
  );
};

export default FeatureCacheAdminPage;
